import { useEffect, useMemo, useRef, useState } from "react";
import type { WorkshopEvent } from "../hooks/useUpcomingWorkshops";

const MONTH_FORMATTER = new Intl.DateTimeFormat("nl-BE", { month: "long", year: "numeric" });
const WEEKDAYS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

type DayCell = {
  date: Date;
  iso: string;
  inMonth: boolean;
  isToday: boolean;
  events: WorkshopEvent[];
};

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildGrid(monthAnchor: Date, events: WorkshopEvent[]): DayCell[] {
  const first = startOfMonth(monthAnchor);
  const dow = (first.getDay() + 6) % 7;
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - dow);

  const todayIso = toIso(new Date());
  const byDate = new Map<string, WorkshopEvent[]>();
  for (const ev of events) {
    const list = byDate.get(ev.datum) ?? [];
    list.push(ev);
    byDate.set(ev.datum, list);
  }

  const cells: DayCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const iso = toIso(d);
    cells.push({
      date: d,
      iso,
      inMonth: d.getMonth() === first.getMonth(),
      isToday: iso === todayIso,
      events: byDate.get(iso) ?? [],
    });
  }
  return cells;
}

type Props = {
  events: WorkshopEvent[];
  selectedId?: string | null;
  onSelect?: (event: WorkshopEvent) => void;
  variant?: "default" | "compact" | "dark";
  busy?: boolean;
  showLabels?: boolean;
};

function firstSelectableMonth(events: WorkshopEvent[]): Date {
  const next = events.find((e) => !e.isFull) ?? events[0];
  if (!next) return startOfMonth(new Date());
  const d = new Date(`${next.datum}T12:00:00`);
  if (Number.isNaN(d.getTime())) return startOfMonth(new Date());
  return startOfMonth(d);
}

export function EventCalendarGrid({
  events,
  selectedId,
  onSelect,
  variant = "default",
  busy,
  showLabels = true,
}: Props) {
  const [monthAnchor, setMonthAnchor] = useState<Date>(() => firstSelectableMonth(events));
  const userOverrodeRef = useRef(false);
  const cells = useMemo(() => buildGrid(monthAnchor, events), [monthAnchor, events]);

  // When events arrive (or change) and the user hasn't manually navigated,
  // jump the calendar to the first selectable workshop's month.
  useEffect(() => {
    if (userOverrodeRef.current) return;
    if (events.length === 0) return;
    const target = firstSelectableMonth(events);
    setMonthAnchor((current) =>
      current.getFullYear() === target.getFullYear() &&
      current.getMonth() === target.getMonth()
        ? current
        : target,
    );
  }, [events]);
  const monthLabel = MONTH_FORMATTER.format(monthAnchor);

  const goPrev = () => {
    userOverrodeRef.current = true;
    setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1));
  };
  const goNext = () => {
    userOverrodeRef.current = true;
    setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1));
  };

  return (
    <div className={`cal-wrap cal-variant-${variant}`}>
      <div className="cal-nav">
        <button
          type="button"
          className="cal-nav-btn"
          onClick={goPrev}
          aria-label="Vorige maand"
        >
          ‹
        </button>
        <div className="cal-nav-label">{monthLabel}</div>
        <button
          type="button"
          className="cal-nav-btn"
          onClick={goNext}
          aria-label="Volgende maand"
        >
          ›
        </button>
      </div>
      <div className="cal-grid" aria-busy={busy}>
        {WEEKDAYS.map((w) => (
          <div key={w} className="cal-weekday">
            {w}
          </div>
        ))}
        {cells.map((cell) => {
          const has = cell.events.length > 0;
          const classes = [
            "cal-day",
            cell.inMonth ? "" : "cal-day-out",
            cell.isToday ? "cal-day-today" : "",
            has ? "cal-day-has" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <div key={cell.iso} className={classes}>
              <div className="cal-day-num">{cell.date.getDate()}</div>
              {cell.events.map((ev) => {
                const isSelected = selectedId === ev.id;
                const tone = ev.isFull ? "is-full" : ev.isAlmostFull ? "is-warn" : "is-open";
                return (
                  <button
                    key={ev.id}
                    type="button"
                    className={`cal-day-event ${tone} ${isSelected ? "is-selected" : ""}`}
                    onClick={() => onSelect?.(ev)}
                    disabled={ev.isFull}
                    title={
                      ev.isFull
                        ? "Volgeboekt — geen plaatsen meer beschikbaar"
                        : ev.isAlmostFull
                          ? `Bijna vol — nog ${ev.plaatsenVrij} ${ev.plaatsenVrij === 1 ? "plaats" : "plaatsen"} vrij`
                          : `Open — ${ev.plaatsenVrij} ${ev.plaatsenVrij === 1 ? "plaats" : "plaatsen"} vrij`
                    }
                    aria-label={
                      ev.isFull
                        ? `${ev.workshop.naam || ev.naam}, volgeboekt`
                        : `${ev.workshop.naam || ev.naam}, ${ev.plaatsenVrij} ${ev.plaatsenVrij === 1 ? "plaats" : "plaatsen"} vrij`
                    }
                  >
                    <span className="cal-day-event-name">
                      {showLabels ? ev.workshop.naam || ev.naam : ""}
                    </span>
                    {showLabels && ev.locatie.naam && (
                      <span className="cal-day-event-loc">{ev.locatie.naam}</span>
                    )}
                    {ev.isFull && <span className="cal-day-event-tag">Volgeboekt</span>}
                    {!ev.isFull && ev.isAlmostFull && (
                      <span className="cal-day-event-tag">Bijna vol</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
